import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";
import { FAQ_GROUPS, ALL_FAQS } from "@/content/faqsAll";
import { useSiteSettings, pick } from "@/lib/siteSettings";

/**
 * /faqs: every buyer question in one place, grouped like a support page.
 * Content lives in content/faqsAll.js (static, so it prerenders and the
 * FAQPage JSON-LD always matches what is on screen).
 */

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);

export default function FAQsPage() {
  const [query, setQuery] = useState("");
  const settings = useSiteSettings();
  const supportEmail = pick(settings, "support_email");

  const q = query.trim().toLowerCase();
  const groups = useMemo(() => {
    if (!q) return FAQ_GROUPS;
    return FAQ_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q) || g.title.toLowerCase().includes(q),
      ),
    })).filter((g) => g.items.length);
  }, [q]);

  return (
    <>
      <Seo
        title="Coconut oil questions, answered"
        description="Answers about Karijeeva cold pressed coconut oil: what it is, purity and lab testing, cooking, hair, skin and baby massage, honest health answers, delivery, payment and returns."
        jsonLd={[
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "FAQs", path: "/faqs" }]),
          faqLd(ALL_FAQS.map((f) => ({ question: f.q, answer: f.a }))),
        ]}
      />

      <div className="bg-brand-parchment" data-testid="page-faqs">
        <header className="max-w-3xl mx-auto px-6 pt-16 sm:pt-24 text-center">
          <p className="font-body text-[15px] font-semibold text-brand-gold">Help</p>
          <h1 className="mt-3 font-display text-[clamp(2.4rem,6vw,4.25rem)] leading-[1.02] tracking-display text-brand-ink">
            Questions, answered.
          </h1>
          <p className="mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed text-ink-muted">
            {ALL_FAQS.length} straight answers about our oil, how to use it, and how ordering works. Can't find yours?{" "}
            <Link to="/contact" className="text-brand-gold hover:underline">Write to us</Link>.
          </p>

          <div className="mt-10 relative max-w-xl mx-auto">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" aria-hidden="true" />
            <label htmlFor="faq-search" className="sr-only">Search questions</label>
            <input
              id="faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions"
              data-testid="faq-search"
              className="w-full h-14 rounded-full bg-white pl-12 pr-5 font-body text-[16px] text-brand-ink placeholder:text-ink-muted shadow-[0_1px_2px_rgba(0,0,0,0.06)] border border-black/10 focus:outline-none focus:ring-2 focus:ring-brand-gold"
            />
          </div>

          {!q && (
            <nav aria-label="FAQ topics" className="mt-8 flex flex-wrap justify-center gap-2">
              {FAQ_GROUPS.map((g) => (
                <a
                  key={g.id}
                  href={`#${g.id}`}
                  className="rounded-control bg-brand-parchment-soft px-4 py-2 font-body text-[14px] text-brand-ink hover:bg-black/5"
                >
                  {g.title}
                </a>
              ))}
            </nav>
          )}
        </header>

        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20 space-y-16">
          {groups.length === 0 && (
            <p className="font-body text-[17px] text-ink-muted text-center" data-testid="faq-empty">
              No questions match &ldquo;{query}&rdquo;.{" "}
              <button type="button" onClick={() => setQuery("")} className="text-brand-gold hover:underline">
                Clear search
              </button>
            </p>
          )}

          {groups.map((g) => (
            <section key={g.id} id={g.id} className="scroll-mt-24" aria-labelledby={`${g.id}-title`}>
              <h2 id={`${g.id}-title`} className="font-display text-[clamp(1.6rem,3.2vw,2.1rem)] leading-tight tracking-heading text-brand-ink">
                {g.title}
              </h2>
              <div className="mt-6 divide-y divide-black/10 border-y border-black/10">
                {g.items.map((f) => {
                  const id = slug(f.q);
                  return (
                    <details key={`${id}${q ? "-s" : ""}`} open={!!q} className="group py-1">
                      <summary
                        className="flex items-start gap-4 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden"
                        data-testid={`faq-q-${id}`}
                      >
                        <h3 className="flex-1 font-display text-[18px] sm:text-[19px] leading-snug text-brand-ink">{f.q}</h3>
                        <ChevronDown className="w-5 h-5 mt-0.5 shrink-0 text-ink-muted transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
                      </summary>
                      <div className="pb-6 pr-9">
                        <p className="font-body text-[16px] sm:text-[17px] leading-relaxed text-ink-muted">{f.a}</p>
                        {f.link && (
                          <Link to={f.link.to} className="mt-3 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                            {f.link.label} <ChevronRight className="w-4 h-4" aria-hidden="true" />
                          </Link>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>
            </section>
          ))}

          <aside className="rounded-3xl bg-brand-parchment-soft p-8 sm:p-10 text-center">
            <p className="font-display text-[26px] leading-tight tracking-heading text-brand-ink">Still have a question?</p>
            <p className="mt-3 font-body text-[16px] leading-relaxed text-ink-muted">
              Write to{" "}
              <a className="text-brand-gold hover:underline" href={`mailto:${supportEmail}`} data-testid="faq-support-email">
                {supportEmail}
              </a>{" "}
              or use our <Link to="/contact" className="text-brand-gold hover:underline">contact form</Link>.
            </p>
          </aside>
        </div>
      </div>
    </>
  );
}
